import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import XionLogo from "../components/XionLogo";
import { api, formatApiError } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F8F6] flex items-center justify-center px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="flex items-center gap-2.5 mb-10">
          <XionLogo size={32} withText />
        </Link>

        {sent ? (
          <div className="glass-card rounded-2xl p-8 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#2060EE] mb-4" />
            <h1 className="font-display font-black text-2xl text-[#0E0E1A] mb-2">Check your inbox</h1>
            <p className="text-[#5A5A72] mb-6">
              If an account exists for <strong>{email}</strong>, a reset link has been sent. Check your spam too.
            </p>
            <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#2060EE] hover:underline">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF]">/ account recovery</p>
            <h1 className="font-display font-black text-4xl text-[#0E0E1A] mt-2 mb-2">Forgot password?</h1>
            <p className="text-[#5A5A72] mb-8">Enter your email and we'll send a reset link.</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Email</label>
                <div className="mt-2 relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9191A8]" />
                  <input
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                    className="w-full pl-11 pr-4 py-3.5 rounded-lg bg-white border border-[rgba(14,14,26,0.12)] focus:border-[#2060EE]/60 focus:ring-2 focus:ring-[#2060EE]/15 outline-none transition text-[#0E0E1A] placeholder:text-[#9191A8]"
                    placeholder="you@studio.com"
                  />
                </div>
              </div>

              {error && (
                <div data-testid="forgot-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  {error}
                </div>
              )}

              <button
                type="submit" disabled={loading}
                data-testid="forgot-submit-btn"
                className="w-full btn-primary py-3.5 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? "Sending..." : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-[#5A5A72]">
              Remembered it?{" "}
              <Link to="/login" data-testid="forgot-to-login" className="text-[#2060EE] hover:underline">Sign in</Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
